import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import StepperHeader from '../components/ui/StepperHeader';
import StepperButtons from '../components/ui/StepperButtons';
import { retreatFlowSteps } from '../domain/retreatFlowSteps';
import { useRegistration } from '../store/RegistrationContext';
import { registrationActionTypes } from '../store/registrationReducer';
import { createStudentDraft, generateStudentId, validateStudent } from '../domain/studentLogic';
import { fetchStudentsForUser } from '../services/studentService';

export default function StudentsPage() {
  const { state, dispatch } = useRegistration();
  const navigate = useNavigate();

  const hasLoadedRef = useRef(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draft, setDraft] = useState(createStudentDraft());
  const [errors, setErrors] = useState({});

  const canContinue = useMemo(() => state.selectedStudentIds.length > 0, [state.selectedStudentIds.length]);

  useEffect(() => {
    const userData = state.auth.user?.userData || state.auth.user;
    const userId = userData?.userId || userData?.user_Id || userData?.UserId || userData?.User_Id;
    if (!userId) return;
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    fetchStudentsForUser(userId)
      .then((students) => {
        students.forEach((s) => {
          dispatch({ type: registrationActionTypes.UPSERT_STUDENT, payload: s });
        });
      })
      .catch(() => {
        // ignore - local CRUD remains available
      });
  }, [dispatch, state.auth.user]);

  if (!state.selectedRetreatId) {
    return <Navigate to="/retreat" replace />;
  }

  const goNext = () => {
    navigate('/sessions');
  };

  const openCreate = () => {
    setDraft(createStudentDraft());
    setErrors({});
    setIsModalOpen(true);
  };

  const openEdit = (student) => {
    setDraft({ ...student });
    setErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const saveStudent = () => {
    const next = { ...draft };
    if (!next.id) {
      next.id = generateStudentId();
    }

    const v = validateStudent(next);
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    dispatch({ type: registrationActionTypes.UPSERT_STUDENT, payload: next });
    if (!state.selectedStudentIds.includes(next.id)) {
      dispatch({ type: registrationActionTypes.TOGGLE_STUDENT_SELECTED, payload: next.id });
    }

    setIsModalOpen(false);
  };

  const toggleSelected = (studentId) => {
    dispatch({ type: registrationActionTypes.TOGGLE_STUDENT_SELECTED, payload: studentId });
  };

  return (
    <>
      <Header />
      <div className="relative min-h-screen bg-gradient-to-br from-indigo-50/70 via-white to-rose-50/70 pb-32 pt-4">

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="overflow-hidden rounded-[28px] bg-white/75 backdrop-blur-xl shadow-[0_26px_70px_-45px_rgba(15,23,42,0.55)] border border-white/60 mb-4 p-4">
            <StepperHeader
              title=""
              subtitle="Add and select attendees"
              steps={retreatFlowSteps}
              activeIndex={1}
              onBack={() => navigate('/retreat')}
            />

            <div className="p-6 md:p-8">
              <div className="rounded-2xl bg-white/90 border border-gray-200 shadow-sm p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-900 mb-1">Add Attendee</div>
                    <div className="text-xs text-gray-600">Select attendees for the retreat.</div>
                  </div>

                  <button
                    onClick={openCreate}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500 px-4 py-2 text-xs font-semibold text-white shadow-lg hover:shadow-xl transition-shadow"
                  >
                    <i className="fa-solid fa-plus"></i>
                    Add Attendee
                  </button>
                </div>

                <div className="mt-5">
                  {state.students.length > 0 ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {state.students
                          .filter((s, index, self) => 
                            index === self.findIndex(t => 
                              t.firstName?.toLowerCase() === s.firstName?.toLowerCase() && 
                              t.lastName?.toLowerCase() === s.lastName?.toLowerCase()
                            )
                          )
                          .map((s) => {
                          const selected = state.selectedStudentIds.includes(s.id);
                          return (
                            <div
                              key={s.id}
                              onClick={() => toggleSelected(s.id)}
                              className={`relative p-0 overflow-hidden rounded-[22px] transition-all duration-300 border ${
                                selected
                                  ? 'cursor-pointer ring-2 ring-amber-400 bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200'
                                  : 'cursor-pointer bg-white/90 hover:bg-white border-gray-200 hover:shadow-xl'
                              }`}
                            >
                              <div className="p-5">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEdit(s);
                                  }}
                                  className="absolute top-4 right-4 w-9 h-9 bg-white/90 hover:bg-white text-gray-700 rounded-full flex items-center justify-center transition-all duration-300 shadow-sm hover:shadow-md z-10 border border-gray-200"
                                  title="Edit Student"
                                >
                                  <i className="fa-solid fa-pen-to-square text-xs"></i>
                                </button>

                                <div className="text-center">
                                  <div className="mx-auto mb-3 h-14 w-14 rounded-full flex items-center justify-center text-2xl text-white shadow-md bg-gradient-to-br from-indigo-600 via-purple-600 to-amber-500">
                                    {s.gender?.toLowerCase() === 'male' || s.gender?.toLowerCase() === 'm' ? '👦' : s.gender?.toLowerCase() === 'female' || s.gender?.toLowerCase() === 'f' ? '👧' : '👤'}
                                  </div>
                                  <h3 className="text-sm font-semibold mb-1 truncate text-gray-900">
                                    {s.firstName} {s.lastName}
                                  </h3>
                                  <div className="text-xs text-gray-600 space-y-1">
                                    {s.relationship && (
                                      <p className="truncate">{s.relationship}</p>
                                    )}
                                  </div>
                                  <div className="mt-3 text-[11px] font-semibold text-gray-500">
                                    {selected ? 'Selected' : 'Tap to select'}
                                  </div>
                                  <div className="mt-2 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                                    <div
                                      className={`h-full rounded-full transition-all ${
                                        selected
                                          ? 'w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500'
                                          : 'w-0 bg-gray-200'
                                      }`}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {state.selectedStudentIds.length > 0 && (
                        <div className="mt-6 rounded-2xl bg-white/80 border border-gray-200 p-4 shadow-sm">
                          <h3 className="text-sm font-semibold text-gray-900 mb-2">
                            Selected Members ({state.selectedStudentIds.length})
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {state.students
                              .filter(s => state.selectedStudentIds.includes(s.id))
                              .map((s) => (
                                <span key={s.id} className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 text-indigo-800 rounded-full text-xs font-semibold border border-indigo-100">
                                  {s.firstName} {s.lastName}
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-12 bg-white/70 rounded-2xl border border-gray-200 shadow-sm">
                      <i className="fa-solid fa-users text-gray-300 text-5xl mb-4"></i>
                      <p className="text-gray-700 text-sm font-semibold mb-1">No attendees found</p>
                      <p className="text-gray-500 text-xs">Click 'Add Attendee' to get started</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <StepperButtons
        backPath="/retreat"
        onNext={goNext}
        nextLabel="Continue to Sessions"
        nextDisabled={!canContinue}
      />

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">
                  {draft.id ? 'Edit Attendee' : 'Add Attendee'}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all duration-200"
                >
                  <i className="fa-solid fa-xmark text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={draft.firstName}
                    onChange={(e) => setDraft((d) => ({ ...d, firstName: e.target.value }))}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.firstName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={draft.lastName}
                    onChange={(e) => setDraft((d) => ({ ...d, lastName: e.target.value }))}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.lastName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={draft.dateOfBirth}
                    onChange={(e) => setDraft((d) => ({ ...d, dateOfBirth: e.target.value }))}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.dateOfBirth && (
                    <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={draft.gender}
                    onChange={(e) => setDraft((d) => ({ ...d, gender: e.target.value }))}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.gender ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.gender && (
                    <p className="text-red-500 text-xs mt-1">{errors.gender}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Relationship <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={draft.relationship}
                    onChange={(e) => setDraft((d) => ({ ...d, relationship: e.target.value }))}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.relationship ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select relationship</option>
                    <option value="Self">Self</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Son">Son</option>
                    <option value="Daughter">Daughter</option>
                    <option value="Parent">Parent</option>
                    <option value="Legal Guardian">Legal Guardian</option>
                    <option value="Family Member">Family Member</option>
                  </select>
                  {errors.relationship && (
                    <p className="text-red-500 text-xs mt-1">{errors.relationship}</p>
                  )}
                </div>
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  onClick={closeModal}
                  className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={saveStudent}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {draft.id ? 'Update' : 'Add'} Member
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
